import React from "react";
import { Box, LinearProgress, Stack, Typography } from "@mui/material";
import { HubIcon } from "../../components";
import { StyledBarTooltip } from "./styles";
import { currency, decimal, VIEW_HOURS } from "./helper";

const pctLabel = (v) => `${Math.round(v)}%`;

const MetricRow = ({ label, value, goal, color, format, isPrimary }) => {
  const pct = goal ? (value / goal) * 100 : 0;
  const clamped = Math.min(pct, 100);

  return (
    <Stack gap="4px">
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" gap="4px" alignItems="center">
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              backgroundColor: color,
            }}
          />
          <Typography variant="caption" sx={{ fontSize: 10 }}>
            {label}
          </Typography>
        </Stack>
        <Typography
          variant="caption"
          sx={{
            fontWeight: isPrimary ? 700 : 600,
            fontSize: isPrimary ? 12 : 11,
          }}
        >
          {pctLabel(pct)}
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={clamped}
        sx={{
          height: isPrimary ? 6 : 4,
          borderRadius: 3,
          backgroundColor: "rgba(0,0,0,0.06)",
          "& .MuiLinearProgress-bar": {
            borderRadius: 3,
            backgroundColor: color,
          },
        }}
      />
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="caption" sx={{ fontSize: 9 }}>
          {format(value)}
        </Typography>
        <Typography variant="caption" sx={{ fontSize: 9 }}>
          {format(goal)}
        </Typography>
      </Stack>
    </Stack>
  );
};

const SchedulerColumnTooltip = ({
  label,
  elements = [],
  weeklyHourGoal = 0,
  weeklyRevenueGoal = 0,
  view = 0,
}) => {
  const totalHours = elements.reduce((sum, e) => sum + (e.hours || 0), 0);
  const totalRevenue = elements.reduce((sum, e) => sum + (e.revenue || 0), 0);
  const count = elements.length;

  const isPrimaryHours = view === VIEW_HOURS;

  return (
    <StyledBarTooltip>
      <Stack
        gap="2px"
        pb="8px"
        borderBottom="1px solid"
        borderColor="divider"
      >
        <Stack direction="row" gap="6px" alignItems="center">
          <HubIcon icon="Calendar" size={11} />
          <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 12 }}>
            {label}
          </Typography>
        </Stack>
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", fontSize: 10, pl: "17px" }}
        >
          {count} work order{count !== 1 ? "s" : ""}
        </Typography>
      </Stack>

      <Stack gap="10px" pt="10px">
        <MetricRow
          label="Wax Hours"
          value={decimal(totalHours)}
          goal={decimal(weeklyHourGoal)}
          color="#DF9E44"
          format={(v) => `${v} hrs`}
          isPrimary={isPrimaryHours}
        />
        <MetricRow
          label="Revenue"
          value={totalRevenue}
          goal={weeklyRevenueGoal}
          color="#CD683E"
          format={currency}
          isPrimary={!isPrimaryHours}
        />
      </Stack>

      {count > 0 && (
        <Stack
          gap="2px"
          pt="8px"
          mt="8px"
          borderTop="1px solid"
          borderColor="divider"
        >
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption" sx={{ fontSize: 9 }}>
              Avg Hours / WO
            </Typography>
            <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 600 }}>
              {decimal(totalHours / count)} hrs
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption" sx={{ fontSize: 9 }}>
              Avg Revenue / WO
            </Typography>
            <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 600 }}>
              {currency(totalRevenue / count)}
            </Typography>
          </Stack>
        </Stack>
      )}
    </StyledBarTooltip>
  );
};

export default SchedulerColumnTooltip;
